import defaults from "lodash/defaults";
import keyBy from "lodash/keyBy";
import orderBy from "lodash/orderBy";
import React from "react";
import { DATE_FORMATS } from "../../../constants";
import ActivityDrawer from "../../../parts/activity-drawer/ActivityDrawer";
import { filterByPath } from "../../../utils/array-helpers";
import { canViewAllOrganizationClients } from "../../../utils/organization-permissions-helpers";
import CoachingDashboardView from "./coaching-dashboard-view";
import { daysSinceValue } from "./coachingHelpers";
import { getSummarySortValue, hasSummaryColumn } from "./monthSummary";

const { DATE_YEAR_FORMAT } = DATE_FORMATS;

const CoachingDashboardController = async ({ res, req, locals, load }) => {
  const momentTZ = locals.momentTZ;
  const { query = {} } = req;
  defaults(query, {
    email: "",
    modal: {},
    filters: {},
    sort: JSON.stringify([[]]),
    month: momentTZ().format(DATE_YEAR_FORMAT),
    filterGroup: "all",
  });
  const {
    filters,
    sort,
    month,
    filterGroup,
    organizationCoach = undefined,
    metric,
  } = query;

  const { organizationId = null } = locals.activeUser;

  if (organizationId) {
    await load("usersOrganization");
  }

  const currentDate = momentTZ();
  locals.today = currentDate.date();
  locals.activeMonth = momentTZ(month, DATE_YEAR_FORMAT);
  const currentM = currentDate.month();
  const currentY = currentDate.year();
  const activeM = locals.activeMonth.month();
  const activeY = locals.activeMonth.year();
  locals.isCurrentMonth = currentM === activeM && activeY === currentY;
  locals.isPreviousMonth =
    (currentY === activeY && currentM > activeM) || currentY > activeY;
  locals.totalDays = locals.activeMonth.daysInMonth();

  const isAllowToViewAllOrganizationsAthletes = canViewAllOrganizationClients(
    locals.organizationRoleName
  );

  // A Team Coach has no personal roster to fall back to — same reason
  // resolveClientListAthletes (athletes.controller.js) defaults them to the
  // org-wide list — so the dashboard must default to every org coach's data
  // too, not just when filterGroup is explicitly switched away from "all".
  const isTeamCoach = locals.organizationRoleName === "Team Coach";

  const organizationCoachId =
    filterGroup !== "all" || isTeamCoach
      ? locals.usersOrganization?.organizationActiveUsers?.map(
          ({ _id }) => _id
        ) || organizationCoach
      : organizationCoach;

  const loadPromises = [];
  if (organizationCoachId) {
    loadPromises.push(
      load("coachOrganizationInformation", { user: organizationCoach })
    );
  }

  loadPromises.push(
    load(
      "coachingDashboardForMonth",
      locals.activeMonth.month() + 1,
      locals.activeMonth.year(),
      organizationCoachId,
      metric
    )
  );

  if (
    locals.activeUser.organizationId &&
    isAllowToViewAllOrganizationsAthletes &&
    !locals.allAthletesFromOrganization
  ) {
    loadPromises.push(load("allAthletesFromOrganization"));
  }

  await Promise.all(loadPromises);

  let allAthletes = locals.coachingDashboardForMonth.result
    ? [...locals.coachingDashboardForMonth.result]
    : [];

  if (filterGroup && filterGroup !== "all" && filterGroup !== "organization") {
    const athletesUsers = locals.allAthletesFromOrganization
      ? keyBy([...locals.allAthletesFromOrganization], "athleteId")
      : locals.coachOrganizationInformation
        ? keyBy([...locals.coachOrganizationInformation.athletes], "_id")
        : keyBy([...locals.activeUser.athletes], "_id");

    allAthletes = allAthletes.filter((athlete) => {
      const fullAthlete = athletesUsers[athlete._id];

      return (
        fullAthlete &&
        ((fullAthlete.groups && fullAthlete.groups.includes(filterGroup)) ||
          (fullAthlete.athleteGroups &&
            fullAthlete.athleteGroups.includes(filterGroup)))
      );
    });
  }

  const [sortFields, sortOrders] = JSON.parse(sort);
  const filteredAthletes = filterByPath(allAthletes, filters);

  if (sortFields.includes("needsWorkoutsOn")) {
    const getNeedsWorkoutsOnValue = (val) => (val === "+30" ? 31 : val);
    const sortOrder = sortOrders[sortFields.indexOf("needsWorkoutsOn")];
    locals.athletes = filteredAthletes.sort((a, b) => {
      const aVal = getNeedsWorkoutsOnValue(a.needsWorkoutsOn);
      const bVal = getNeedsWorkoutsOnValue(b.needsWorkoutsOn);
      return sortOrder === "desc" ? bVal - aVal : aVal - bVal;
    });
  } else if (sortFields.includes("lastLoggedDays")) {
    const sortOrder = sortOrders[sortFields.indexOf("lastLoggedDays")];
    locals.athletes = filteredAthletes.sort((a, b) => {
      const aVal = daysSinceValue(a);
      const bVal = daysSinceValue(b);
      if (aVal === bVal) return 0;
      return sortOrder === "desc" ? bVal - aVal : aVal - bVal;
    });
  } else if (sortFields.includes("summary") && hasSummaryColumn(metric)) {
    const sortOrder = sortOrders[sortFields.indexOf("summary")];
    const summaryOf = (athlete) =>
      getSummarySortValue({ athlete, metric, unitSelected: "metric" });
    locals.athletes = filteredAthletes.sort((a, b) => {
      const aVal = summaryOf(a);
      const bVal = summaryOf(b);
      if (aVal === bVal) return 0;
      if (aVal === null) return 1;
      if (bVal === null) return -1;
      return sortOrder === "desc" ? bVal - aVal : aVal - bVal;
    });
  } else {
    locals.athletes = orderBy(filteredAthletes, sortFields, sortOrders);
  }

  // "No activity in N+ days"; athletes who never logged always match.
  const minDaysSince = Number(query.daysSince);
  if (minDaysSince > 0) {
    locals.athletes = locals.athletes.filter(
      (athlete) => daysSinceValue(athlete) >= minDaysSince
    );
  }

  //filter by name and email using regexp
  if (query.name) {
    locals.athletes = locals.athletes.filter((athlete) => {
      return (
        new RegExp("^.*" + query.name + ".*$", "gi").test(athlete.name) ||
        new RegExp("^.*" + query.name + ".*$", "gi").test(athlete.email)
      );
    });
  }

  const modal = query.modal || {};

  locals.modal = {
    "activity-feed": <ActivityDrawer />,
  }[modal.type];
  res.body = <CoachingDashboardView />;
};

export default CoachingDashboardController;
